import React from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Chip,
    LinearProgress,
    Grid,
    Alert,
    Divider,
    IconButton
} from '@mui/material';
import {
    TrendingUp,
    Star,
    Speed,
    CompareArrows,
    Email,
    Schedule,
    CheckCircle,
    OpenInNew
} from '@mui/icons-material';

interface RegressionData {
    id: number;
    artist_id: string;
    name: string;
    email: string;
    priority_score: number;
    momentum_score: number;
    readout_text: string;
    contacted_status: string;
    created_at: string;
    outreach_readout: string;
    reference_artist_name: string;
    reference_artist_listeners: number;
    reference_artist_2_name: string;
    reference_artist_2_listeners: number;
    reference_artist_3_name: string;
    reference_artist_3_listeners: number;
    spotify_monthly_listeners: number;
    spotify_followers: number;
    spotify_growth_rate: number;
}

// Helper function for formatting numbers
const formatNumber = (num?: number): string => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

// Helper function for priority color
const getPriorityColor = (score: number): 'error' | 'warning' | 'info' => {
    if (score >= 10) return 'error';
    if (score >= 8.5) return 'warning';
    return 'info';
};

// Helper function for status color
const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
        case 'pending': return 'warning';
        case 'contacted': return 'primary';
        case 'completed': return 'success';
        default: return 'default';
    }
};

interface NextBigThingAlertProps {
    regressionData: RegressionData;
}

export const NextBigThingAlert: React.FC<NextBigThingAlertProps> = ({ regressionData }) => {
    const priorityColor = getPriorityColor(regressionData.priority_score);
    
    return (
        <Alert 
            severity={priorityColor} 
            sx={{ mb: 2 }}
            icon={<TrendingUp />}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="subtitle2" fontWeight="bold">
                        🚨 REGRESSION PROSPECT - NEXT BIG THING CANDIDATE
                    </Typography>
                    <Typography variant="body2">
                        Priority Score: <strong>{regressionData.priority_score.toFixed(1)}/10</strong> • 
                        Momentum: <strong>{regressionData.momentum_score.toFixed(1)}</strong>
                    </Typography>
                </Box>
                <Chip 
                    label={`TOP ${regressionData.priority_score >= 10 ? '2%' : regressionData.priority_score >= 9 ? '10%' : '25%'}`}
                    color={priorityColor}
                    size="small"
                />
            </Box>
        </Alert>
    );
};

interface GrowthPredictionPanelProps {
    regressionData: RegressionData;
}

export const GrowthPredictionPanel: React.FC<GrowthPredictionPanelProps> = ({ regressionData }) => {
    const growthRate = regressionData.spotify_growth_rate || 0;
    
    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Speed color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Growth Prediction Analysis
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Current Metrics
                        </Typography>
                        
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Spotify Listeners:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatNumber(regressionData.spotify_monthly_listeners)}
                                </Typography>
                            </Box>
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2">Spotify Followers:</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {formatNumber(regressionData.spotify_followers)}
                                </Typography>
                            </Box>
                            {growthRate > 0 && (
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2">Growth Rate:</Typography>
                                    <Typography variant="body2" fontWeight="bold" color="success.main">
                                        +{(growthRate * 100).toFixed(1)}%
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                            Momentum Indicators
                        </Typography>
                        
                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="body2">Priority Score</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {regressionData.priority_score.toFixed(1)}/10
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(regressionData.priority_score / 10) * 100}
                                sx={{ height: 6, borderRadius: 3 }}
                                color={getPriorityColor(regressionData.priority_score)}
                            />
                        </Box>

                        <Box mb={2}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                <Typography variant="body2">Momentum Score</Typography>
                                <Typography variant="body2" fontWeight="bold">
                                    {regressionData.momentum_score.toFixed(1)}/5
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={(regressionData.momentum_score / 5) * 100}
                                sx={{ height: 6, borderRadius: 3 }}
                                color="info"
                            />
                        </Box>
                    </Grid>
                </Grid>

                {/* AI Analysis */}
                <Box mt={2} pt={2} borderTop={1} borderColor="grey.200">
                    <Typography variant="body2" fontStyle="italic" color="textSecondary">
                        "{regressionData.readout_text}"
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

interface ReferenceArtistComparisonsProps {
    regressionData: RegressionData;
}

export const ReferenceArtistComparisons: React.FC<ReferenceArtistComparisonsProps> = ({ regressionData }) => {
    const referenceArtists = [
        {
            name: regressionData.reference_artist_name,
            listeners: regressionData.reference_artist_listeners
        },
        {
            name: regressionData.reference_artist_2_name,
            listeners: regressionData.reference_artist_2_listeners
        },
        {
            name: regressionData.reference_artist_3_name,
            listeners: regressionData.reference_artist_3_listeners
        }
    ].filter(artist => artist.name && artist.listeners > 0);

    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <CompareArrows color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Similar Artist Trajectory
                    </Typography>
                </Box>

                <Typography variant="body2" color="textSecondary" mb={2}>
                    Based on growth patterns, audience engagement, and platform metrics
                </Typography>

                {referenceArtists.map((artist, index) => (
                    <Box key={artist.name} mb={2}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Box display="flex" alignItems="center">
                                <Chip 
                                    label={`#${index + 1}`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mr: 1, minWidth: 40 }}
                                />
                                <Typography variant="body1" fontWeight="medium">
                                    {artist.name}
                                </Typography>
                            </Box>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                                {formatNumber(artist.listeners)} listeners
                            </Typography>
                        </Box>
                        
                        {/* Growth potential indicator */}
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="caption" color="textSecondary">
                                Potential trajectory:
                            </Typography>
                            <Typography variant="caption" fontWeight="bold">
                                {Math.round((artist.listeners / regressionData.spotify_monthly_listeners) * 100) / 100}x current size
                            </Typography>
                        </Box>
                        
                        {index < referenceArtists.length - 1 && <Divider sx={{ mt: 2 }} />}
                    </Box>
                ))}

                {/* Outreach insight */}
                <Box mt={2} pt={2} borderTop={1} borderColor="grey.200">
                    <Typography variant="body2" fontStyle="italic" color="textSecondary">
                        "{regressionData.outreach_readout}"
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

interface ContactStatusTrackingProps {
    regressionData: RegressionData;
}

export const ContactStatusTracking: React.FC<ContactStatusTrackingProps> = ({ regressionData }) => {
    const createdDate = new Date(regressionData.created_at);
    const daysSinceCreated = Math.floor((Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return (
        <Card>
            <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                    <Email color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">
                        Contact Status
                    </Typography>
                </Box>

                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <Box display="flex" alignItems="center" gap={1} mb={2}>
                            <Chip 
                                label={regressionData.contacted_status.toUpperCase()}
                                color={getStatusColor(regressionData.contacted_status)}
                                size="small"
                            />
                            <Typography variant="body2" color="textSecondary">
                                {daysSinceCreated} days ago
                            </Typography>
                        </Box>

                        <Box display="flex" flexDirection="column" gap={1}>
                            <Typography variant="body2">
                                <strong>Email:</strong> {regressionData.email}
                            </Typography>
                            <Typography variant="body2">
                                <strong>Added:</strong> {createdDate.toLocaleDateString()}
                            </Typography>
                            {regressionData.contacted_status === 'pending' && (
                                <Typography variant="body2" color="warning.main">
                                    <strong>Action needed:</strong> Send initial outreach
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                            Quick Actions
                        </Typography>
                        
                        <Box display="flex" flexDirection="column" gap={1}>
                            {regressionData.contacted_status === 'pending' && (
                                <Typography variant="body2">
                                    📧 Draft outreach email ready
                                </Typography>
                            )}
                            <Typography variant="body2">
                                📊 Monitor growth signals
                            </Typography>
                            <Typography variant="body2">
                                🎯 Schedule follow-up reminder
                            </Typography>
                            <Typography variant="body2">
                                📈 Add to priority playlist
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};